import React from 'react';

/**
 * A confirm dialog shaped exactly like scale-portal's shared ConfirmDialog.
 *
 * WHY THIS EXISTS
 * ---------------
 * This app has no UI kit, so every confirm here was hand-rolled — and each one
 * drifted a little from the next and further from scale-portal: some had a
 * close cross and some did not, some put the buttons on the white body while
 * scale-portal puts them on a grey strip across the foot, sizes differed. A
 * case manager and the mediator they work for answer the same questions about
 * the same records; the dialogs should not look like two products.
 *
 * So the structure below is scale-portal's, element for element:
 *
 *   white body   px-6 pb-6 pt-2, title as an h5 with mb-2, then the message
 *   grey strip   bg-gray-100 px-6 py-3, rounded at the two bottom corners
 *   close cross  top-right, from the Dialog chrome there
 *
 * Match it on both sides when either changes.
 *
 * NO ICON. The title already says what the dialog is, and a status badge
 * restates alarm the rest of this feature was deliberately changed to stop
 * making.
 */

const BTN =
  'h-9 inline-flex items-center justify-center rounded-md px-4 text-sm transition disabled:opacity-50';

const CONFIRM_COLOURS = {
  /* red-600 for destructive, teal-800 for everything else — the only two
   * confirm colours scale-portal uses across its fourteen call sites. */
  danger: 'bg-red-600 font-semibold text-white hover:bg-red-700',
  primary: 'bg-teal-800 font-semibold text-white hover:bg-teal-900',
};

const ConfirmDialog = ({
  isOpen,
  title,
  children,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  /* 'danger' | 'primary' */
  tone = 'danger',
  loading = false,
  /* Centres the action strip. Off by default, matching scale-portal, where
   * only the save-anyway confirm opts in. */
  centerActions = false,
  onCancel,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !loading && onCancel?.()}
      />
      <div className="relative z-10 w-[480px] max-w-[92vw] overflow-hidden rounded-lg bg-white shadow-2xl">
        <button
          type="button"
          aria-label="Close"
          disabled={loading}
          onClick={() => onCancel?.()}
          className="absolute right-4 top-4 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="px-6 pb-6 pt-5">
          <h5 className="mb-2 pr-8 text-lg font-semibold text-gray-900">
            {title}
          </h5>
          <div className="text-sm text-gray-600">{children}</div>
        </div>

        <div
          className={`${
            centerActions ? 'text-center' : 'text-right'
          } bg-gray-100 px-6 py-3`}
        >
          <button
            type="button"
            disabled={loading}
            onClick={() => onCancel?.()}
            className={`${BTN} mr-2 border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50`}
          >
            {cancelText}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => onConfirm?.()}
            className={`${BTN} ${CONFIRM_COLOURS[tone] || CONFIRM_COLOURS.danger}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
